import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { cardService } from '../../../services/cardService';
import { CardPreview } from '../../../components/editor/CardPreview';
import { TabBasics } from '../../../components/editor/tabs/TabBasics';
import { TabContact } from '../../../components/editor/tabs/TabContact';
import { TabLinks } from '../../../components/editor/tabs/TabLinks';
import { TabActions } from '../../../components/editor/tabs/TabActions';
import { TabStyle } from '../../../components/editor/tabs/TabStyle';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { Alert } from '../../../components/ui/Alert';
import { ShareModal } from '../../../components/app/ShareModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const Modal = ({ title, description, onConfirm, onCancel, confirmText = 'Confirm', variant = 'primary' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl animate-in zoom-in-95 duration-200">
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 mb-6 whitespace-pre-wrap">{description}</p>
      <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
        <Button type="button" variant="ghost" onClick={onCancel} className="w-full sm:w-auto">Cancel</Button>
        <Button type="button" variant={variant} onClick={onConfirm} className="w-full sm:w-auto">{confirmText}</Button>
      </div>
    </div>
  </div>
);

export const CardEditor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [card, setCard] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basics');
  const [originalSlug, setOriginalSlug] = useState('');

  // Modals state
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [showSlugWarning, setShowSlugWarning] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const cardData = await cardService.getCard(id, user.id);
        const linksData = await cardService.getLinks(id);
        setCard(cardData);
        setLinks(linksData);
        setOriginalSlug(cardData.public_slug);
      } catch (err) {
        setError('Failed to load card details.');
      } finally {
        setLoading(false);
      }
    };
    if (user?.id && id) loadData();
  }, [user, id]);

  const updateCard = (field, value) => {
    setCard(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (forceSave = false) => {
    if (!forceSave && card.status === 'published' && card.public_slug !== originalSlug) {
      setShowSlugWarning(true);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { id: cardId, user_id, created_at, updated_at, ...updates } = card;
      await cardService.updateCard(id, user.id, updates);
      await cardService.saveLinks(id, links);
      setOriginalSlug(card.public_slug);
      setShowSlugWarning(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!card.full_name?.trim() || !card.job_title?.trim()) {
      setError("Public full name and job title are required to publish. Please check the Basics tab.");
      setShowPublishConfirm(false);
      return;
    }
    setSaving(true);
    try {
      await cardService.updateCard(id, user.id, { status: 'published' });
      setCard(prev => ({ ...prev, status: 'published' }));
      setShowPublishConfirm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    setSaving(true);
    try {
      await cardService.updateCard(id, user.id, { status: 'draft' });
      setCard(prev => ({ ...prev, status: 'draft' }));
      setShowUnpublishConfirm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basics', label: 'Basics', icon: FiIcons.FiUser },
    { id: 'contact', label: 'Contact', icon: FiIcons.FiMail },
    { id: 'links', label: 'Links', icon: FiIcons.FiLink },
    { id: 'actions', label: 'Actions', icon: FiIcons.FiPlayCircle },
    { id: 'style', label: 'Style', icon: FiIcons.FiLayout }
  ];

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!card) return <div className="text-center py-20"><Alert variant="error" message={error || "Card not found"} /></div>;

  const publicUrl = `${window.location.origin}${window.location.pathname}#/c/${card.public_slug}`;

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-6">
      
      {showPublishConfirm && (
        <Modal
          title="Publish this card?"
          description={`Your card will become publicly accessible at:\n\n${publicUrl}`}
          onConfirm={handlePublish}
          onCancel={() => setShowPublishConfirm(false)}
          confirmText="Publish Card"
        />
      )}

      {showUnpublishConfirm && (
        <Modal
          title="Unpublish Card"
          description="The card will no longer be publicly accessible. Existing links will stop working. The card will remain saved as a draft."
          onConfirm={handleUnpublish}
          onCancel={() => setShowUnpublishConfirm(false)}
          confirmText="Unpublish"
          variant="danger"
        />
      )}

      {showSlugWarning && (
        <Modal
          title="Change public link?"
          description="Changing the slug will change the card's public URL. Any previously shared link will stop working."
          onConfirm={() => handleSave(true)}
          onCancel={() => setShowSlugWarning(false)}
          confirmText="Confirm Change"
          variant="danger"
        />
      )}

      {showShareModal && (
        <ShareModal card={card} user={user} onClose={() => setShowShareModal(false)} />
      )}

      {/* Left Panel: Editor */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full min-h-[600px]">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50 gap-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/app/cards')} className="mr-4 text-slate-400 hover:text-slate-900">
              <SafeIcon icon={FiIcons.FiArrowLeft} className="text-xl" />
            </button>
            <div>
              <h1 className="font-bold text-slate-900 truncate max-w-[200px]">{card.internal_name}</h1>
              {card.status === 'published' ? (
                <div className="flex items-center text-xs text-green-600 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></span> Published
                </div>
              ) : (
                <div className="flex items-center text-xs text-slate-500 font-medium capitalize">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-1.5"></span> {card.status}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {card.status === 'published' && (
              <Button variant="secondary" size="sm" onClick={() => setShowShareModal(true)}>
                <SafeIcon icon={FiIcons.FiShare2} className="mr-1.5 hidden sm:inline" /> Share
              </Button>
            )}
            {card.status === 'published' ? (
              <Button variant="ghost" size="sm" onClick={() => setShowUnpublishConfirm(true)}>Unpublish</Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setShowPublishConfirm(true)}>Publish</Button>
            )}
            <Button size="sm" onClick={() => handleSave(false)} isLoading={saving}>Save Changes</Button>
          </div>
        </div>

        <div className="flex border-b border-slate-100 overflow-x-auto hide-scrollbar">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === t.id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <SafeIcon icon={t.icon} className="mr-2" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && <Alert variant="error" message={error} className="mb-6" />}
          {activeTab === 'basics' && <TabBasics card={card} updateCard={updateCard} />}
          {activeTab === 'contact' && <TabContact card={card} updateCard={updateCard} />}
          {activeTab === 'links' && <TabLinks links={links} setLinks={setLinks} />}
          {activeTab === 'actions' && <TabActions card={card} updateCard={updateCard} />}
          {activeTab === 'style' && <TabStyle card={card} updateCard={updateCard} />}
        </div>
      </div>

      {/* Right Panel: Live Preview */}
      <div className="hidden lg:flex w-[400px] flex-col items-center justify-center bg-slate-100 rounded-2xl border border-slate-200 p-6 overflow-hidden">
        <CardPreview card={card} links={links} mode="preview" />
      </div>
    </div>
  );
};